/**
 * API service for communicating with the backend
 */

import {
  CreateJobResponse,
  JobStatusResponse,
} from '../types';
import { getFileMimeType } from '../utils/fileValidation';

// API Gateway endpoint - should be configured via environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Creates a new job and returns upload URL
 */
export async function createJob(
  candidateName: string,
  objective: string,
  fileName: string,
  fileType: string,
  outputFormat: 'pdf' | 'docx' = 'pdf'
): Promise<CreateJobResponse> {
  const response = await fetch(`${API_BASE_URL}/jobs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name: candidateName,
      objective,
      fileName,
      fileType,
      outputFormat,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to create job: ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * Uploads file to pre-signed S3 URL
 */
export async function uploadFile(
  uploadUrl: string,
  file: File
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': getFileMimeType(file),
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file: ${response.statusText}`);
  }
}

/**
 * Gets job status
 */
export async function getJobStatus(
  jobId: string,
  wait: boolean = false
): Promise<JobStatusResponse> {
  const url = new URL(`${API_BASE_URL}/jobs/${jobId}`);
  if (wait) {
    url.searchParams.set('wait', 'true');
  }

  const response = await fetch(url.toString());

  if (response.status === 404) {
    throw new Error('Job not found');
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to get job status: ${response.statusText}`
    );
  }

  return response.json();
}

/**
 * Downloads the output PDF
 */
export async function downloadPDF(
  downloadUrl: string,
  fileName: string
): Promise<void> {
  const response = await fetch(downloadUrl);

  if (!response.ok) {
    throw new Error(`Failed to download PDF: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
